import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { AgreementStatus, IAgreement, SinisterAgreementService } from "../../../../shared/services/api/sinister/SinisterAgreementService";
import { SinisterInternalFollowUpService } from "../../../../shared/services/api/sinister/SinisterInternalFollowUpService";
import { trackAsync } from "../../../../shared/utils/telemetry";
import { AGREEMENT_STATUS_OPTIONS, getAgreementStatusChipColors, getAgreementStatusLabel } from "../../constants/agreementStatusOptions";

interface Props {
	open: boolean;
	agreement: IAgreement;
	onClose: () => void;
	onConfirm: () => void;
}

export const ModalUpdateAgreementStatus = ({ open, agreement, onClose, onConfirm }: Props) => {
	const toastHandler = useToast();
	const [status, setStatus] = useState<AgreementStatus>(agreement.status);
	const [reason, setReason] = useState("");
	const [reasonError, setReasonError] = useState(false);
	const [generateFollowUp, setGenerateFollowUp] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const currentColors = getAgreementStatusChipColors(agreement.status);

	useEffect(() => {
		if (open) {
			setStatus(agreement.status);
			setReason(agreement.cancelReason ?? "");
			setReasonError(false);
			setGenerateFollowUp(true);
		}
	}, [open, agreement]);

	const handleConfirm = async () => {
		if (status === "broken" && !reason.trim()) {
			setReasonError(true);
			return;
		}

		setIsSubmitting(true);
		const result = await trackAsync(
			"agreement.update_status",
			() =>
				SinisterAgreementService.updateStatus(agreement.id, {
					status,
					reason: status === "broken" ? reason.trim() : undefined,
				}),
			{ agreementId: agreement.id, sinisterId: agreement.sinisterId }
		);

		if (result instanceof Error) {
			setIsSubmitting(false);
			toastHandler.present({ type: "error", messageOrContent: result.message, position: "top-right" });
			return;
		}

		if (generateFollowUp) {
			// Ação secundária: se falhar, não deve derrubar o sucesso da ação principal (status já foi salvo).
			try {
				await trackAsync(
					"followup.internal_note",
					() =>
						SinisterInternalFollowUpService.addSinisterInternalFollowupNote(
							agreement.sinisterId,
							`<p>Status do acordo atualizado para: ${getAgreementStatusLabel(status)}</p>`
						),
					{ sinisterId: agreement.sinisterId, agreementId: agreement.id }
				);
			} catch (error) {
				console.error("Falha ao gerar follow-up automático de status do acordo:", error);
			}
		}

		setIsSubmitting(false);
		toastHandler.present({
			type: "success",
			messageOrContent: `Status do acordo atualizado para "${getAgreementStatusLabel(status)}".`,
			position: "top-right",
		});
		onConfirm();
	};

	return (
		<>
			<LoadingComp isopen={isSubmitting} />
			<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
						Alterar Status do Acordo
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Stack spacing={2} mt={0.5}>
						<Box>
							<Typography variant="body2" color="text.secondary" mb={0.5}>
								Status atual
							</Typography>
							<Chip
								size="small"
								label={getAgreementStatusLabel(agreement.status)}
								sx={{ bgcolor: currentColors.bg, color: currentColors.color, fontWeight: 500 }}
							/>
						</Box>

						<TextField select fullWidth label="Novo status" value={status} onChange={(e) => setStatus(e.target.value as AgreementStatus)}>
							{AGREEMENT_STATUS_OPTIONS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</TextField>

						{status === "broken" && (
							<TextField
								fullWidth
								multiline
								minRows={3}
								label="Motivo da quebra de acordo"
								value={reason}
								onChange={(e) => {
									setReason(e.target.value);
									if (e.target.value.trim()) setReasonError(false);
								}}
								error={reasonError}
								helperText={reasonError ? "Informe o motivo da quebra de acordo." : ""}
								required
							/>
						)}

						<FormControlLabel
							control={<Checkbox checked={generateFollowUp} onChange={(e) => setGenerateFollowUp(e.target.checked)} />}
							label="Gerar Follow Up"
						/>
					</Stack>
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>Voltar</Button>
					<Button variant="contained" disabled={isSubmitting || status === agreement.status} onClick={handleConfirm}>
						Confirmar Alteração
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
